import type { Database } from "@pex/database/types";
import type { SupabaseClient } from "@supabase/supabase-js";

export interface PaystackAuthorizationRow {
  account_id: string;
  authorization_code: string;
  bank: string | null;
  brand: string;
  card_type: string | null;
  country_code: string | null;
  created_at: string;
  exp_month: string;
  exp_year: string;
  id: number;
  last4: string;
  reusable: boolean;
  updated_at: string;
}

export interface UpsertAuthorizationInput {
  authorization_code: string;
  bank?: string | null;
  brand: string;
  card_type?: string | null;
  country_code?: string | null;
  exp_month: string;
  exp_year: string;
  last4: string;
  reusable?: boolean;
}

function sharedSchema(client: SupabaseClient<Database>) {
  // biome-ignore lint/suspicious/noExplicitAny: schema() is not typed in the generated Database type
  return (client as unknown as any).schema("shared");
}

/**
 * Returns the most recent valid Paystack authorizations for an account.
 *
 * "Valid" means reusable=true and the card has not expired.
 * Results are ordered newest-first so callers can take the first entry as the
 * default for POST /subscription (matches Paystack's own default behaviour).
 */
export async function getValidAuthorizations(
  client: SupabaseClient<Database>,
  accountId: string
): Promise<PaystackAuthorizationRow[]> {
  const now = new Date();
  const currentYear = now.getFullYear().toString();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");

  const { data, error } = await sharedSchema(client)
    .from("paystack_authorizations")
    .select(
      "id, account_id, authorization_code, last4, brand, exp_month, exp_year, card_type, bank, country_code, reusable, created_at, updated_at"
    )
    .eq("account_id", accountId)
    .eq("reusable", true)
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(
      `Failed to fetch Paystack authorizations: ${error.message}`
    );
  }

  if (!data) {
    return [];
  }

  // Filter expired cards in application code — Supabase JS doesn't support
  // compound OR conditions on two columns cleanly without RPC.
  return (data as PaystackAuthorizationRow[]).filter((row) => {
    const year = row.exp_year;
    const month = row.exp_month.padStart(2, "0");
    if (year > currentYear) {
      return true;
    }
    if (year === currentYear && month >= currentMonth) {
      return true;
    }
    return false;
  });
}

/**
 * Upserts a Paystack authorization for an account.
 *
 * Safe to call on every subscription.create / charge.success webhook — the
 * unique constraint on (account_id, authorization_code) prevents duplicates.
 * On conflict, card metadata is updated in case expiry or brand info changed.
 */
export async function upsertPaystackAuthorization(
  client: SupabaseClient<Database>,
  accountId: string,
  auth: UpsertAuthorizationInput
): Promise<void> {
  const { error } = await sharedSchema(client)
    .from("paystack_authorizations")
    .upsert(
      {
        account_id: accountId,
        authorization_code: auth.authorization_code,
        last4: auth.last4,
        brand: auth.brand,
        exp_month: auth.exp_month,
        exp_year: auth.exp_year,
        card_type: auth.card_type ?? null,
        bank: auth.bank ?? null,
        country_code: auth.country_code ?? null,
        reusable: auth.reusable ?? true,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "account_id, authorization_code" }
    );

  if (error) {
    throw new Error(
      `Failed to upsert Paystack authorization: ${error.message}`
    );
  }
}
